import { verificationCodeSchema } from '@school-erp/validation';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../../api/http';
import { Notice, primaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import type { CertType } from './shared';

interface Check {
  valid: boolean;
  status: 'issued' | 'revoked';
  type: CertType;
  number: string;
  issueDate: string;
  schoolName: string;
  studentName: string;
}

/** Public page: anyone with a certificate in hand can check it. It shows no more than the paper does. */
export function VerifyCertificatePage() {
  const { code: param } = useParams();
  const navigate = useNavigate();
  const [typed, setTyped] = useState(param ?? '');
  const parsed = param ? verificationCodeSchema.safeParse(param) : null;
  const code = parsed?.success ? parsed.data : null;

  const check = useQuery({
    queryKey: ['verify', code],
    queryFn: () => api<Check>(`/public/certificates/${code}`, { anonymous: true }),
    enabled: Boolean(code),
    retry: false,
  });

  return (
    <main className="mx-auto max-w-xl space-y-4 p-6">
      <h1 className="text-2xl font-bold">{t('verify.title')}</h1>
      <p className="text-slate-600">{t('verify.intro')}</p>
      <form
        noValidate
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void navigate(`/verify/${encodeURIComponent(typed.trim())}`);
        }}
      >
        <TextField
          label={t('verify.code')}
          value={typed}
          maxLength={20}
          autoComplete="off"
          onChange={(e) => setTyped(e.target.value)}
        />
        <button type="submit" className={primaryButton}>
          {t('verify.check')}
        </button>
      </form>
      {param && !code && <Notice kind="error">{t('verify.badCode')}</Notice>}
      {check.isError && (
        <Notice kind="error">
          {check.error instanceof ApiError && check.error.status === 404
            ? t('verify.notFound')
            : t('common.actionError')}
        </Notice>
      )}
      {check.data && (
        <section
          aria-label={t('verify.title')}
          className="space-y-2 rounded-lg border border-slate-200 bg-white p-4"
        >
          <Notice kind={check.data.valid ? 'ok' : 'error'}>
            {check.data.valid ? t('verify.valid') : t('verify.revoked')}
          </Notice>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-slate-600">{t('verify.kind')}</dt>
            <dd>{t(`cert.type.${check.data.type}` as MessageKey)}</dd>
            <dt className="text-slate-600">{t('verify.number')}</dt>
            <dd>{check.data.number}</dd>
            <dt className="text-slate-600">{t('verify.holder')}</dt>
            <dd>{check.data.studentName}</dd>
          </dl>
          <p className="text-sm text-slate-600">
            {t('verify.issuedBy', { school: check.data.schoolName, date: check.data.issueDate })}
          </p>
        </section>
      )}
    </main>
  );
}
